import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { UNSAFE_NavigationContext } from 'react-router-dom';
import PromptEngineerDraft from './PromptEngineerDraft';
import PromptEngineerInterview from './PromptEngineerInterview';
import PromptEngineerLanding from './PromptEngineerLanding';
import PromptEngineerOptimize from './PromptEngineerOptimize';
import PromptEngineerOptimizedDraft from './PromptEngineerOptimizedDraft';
import PromptEngineerProgress from './PromptEngineerProgress';
import PromptEngineerReview from './PromptEngineerReview';
import {
    buildDefaultInstructionName,
    buildPlaceholderInstructions,
    createInitialPromptEngineerConfig,
    isPhaseValid,
    updatePromptEngineerField,
    updatePromptEngineerRootField
} from './promptEngineerUtils';
import {
    generateBusinessDescription,
    generatePromptInstructions,
    getBusinessActionInterviewMetadata,
    getPromptEngineerGenerationSession
} from '../../../services/promptEngineerService';
import { optimizePromptInstructions } from '../../../services/promptEngineerOptimizationService';

const LOG_TAG = '[AIPromptEngineer]';
const isDevelopment = process.env.NODE_ENV === 'development';
const isJsonReviewDebugFlagEnabled = String(process.env.REACT_APP_PE_JSON_REVIEW_DEBUG || '').toLowerCase() === 'true';
const ACTION_PHASE_INDEX = 5;
const SUCCESS_PHASE_INDEX = 6;
const FULL_PHASE_SEQUENCE = [0, 1, 2, 3, 4, ACTION_PHASE_INDEX, SUCCESS_PHASE_INDEX];
const ONBOARDING_PHASE_SEQUENCE = [0, 1, 2, 3, 4, SUCCESS_PHASE_INDEX];
const RESUMABLE_STAGES = new Set(['landing', 'interview', 'review', 'progress', 'draft', 'optimize', 'optimizedDraft']);
const ONBOARDING_RECOVERY_MIN_PROGRESS_VISIBLE_MS = 1800;

const summarizeConfig = (promptEngineerConfig) => ({
    source: promptEngineerConfig?.source || '',
    version: promptEngineerConfig?.version || '',
    industry: promptEngineerConfig?.business?.industry || '',
    audience: promptEngineerConfig?.business?.audience || '',
    mainJob: promptEngineerConfig?.role?.mainJob || '',
    businessDescriptionLength: promptEngineerConfig?.business?.description?.trim?.().length || 0,
    selectedBehaviorsCount: promptEngineerConfig?.behavior?.selectedBehaviors?.length || 0,
    tasksCount: promptEngineerConfig?.role?.tasks?.length || 0,
    customTasksLength: promptEngineerConfig?.role?.customTasks?.trim?.().length || 0,
    goalsCount: promptEngineerConfig?.success?.goals?.length || 0,
    prohibitedActionsCount: promptEngineerConfig?.success?.prohibitedActions?.length || 0,
    escalationTriggersCount: promptEngineerConfig?.success?.escalationTriggers?.length || 0,
    businessActionSelectionsCount: promptEngineerConfig?.businessActionSelections?.length || 0
});

const devLog = (eventName, payload) => {
    if (!isDevelopment) return;
    console.log(LOG_TAG, eventName, payload);
};

const normalizeString = (value) => (typeof value === 'string' ? value.trim() : '');

const buildPersistenceSnapshot = ({
    stage,
    currentPhase,
    config,
    instructionName,
    draftText,
    optimizedText,
    generationSessionKey,
    activeDraftVersion
}) => JSON.stringify({
    stage,
    currentPhase,
    config,
    instructionName: normalizeString(instructionName),
    draftText: normalizeString(draftText),
    optimizedText: normalizeString(optimizedText),
    generationSessionKey: normalizeString(generationSessionKey),
    activeDraftVersion: activeDraftVersion === 'optimized' ? 'optimized' : 'original'
});

const resolveContextMessage = (flowContext) => {
    if (flowContext === 'agent_edit_revision') {
        return 'This revision is saved as a draft. Your agent will keep using the current published instructions until you publish this revision.';
    }
    if (flowContext === 'agent_new_standalone') {
        return 'Your draft will be saved for later. This agent will continue using its currently selected instructions.';
    }
    return '';
};

export default function PromptEngineerFlow({
    onExit,
    onSaveInstruction,
    onSaveForLater,
    isSaving,
    initialConfig = null,
    initialInstructionName = '',
    initialDraftText = '',
    existingInstructionId = null,
    reopenMode = false,
    onboardingRecoveryMode = false,
    flowContext = 'instructions',
    onUnsavedChange,
    resumeSnapshot = null,
    onSaveForLaterRequestChange,
    onResumeSnapshotChange,
    onResumeSnapshotClear,
    showInlineSaveForLaterAction = true
}) {
    const resolvedInitialConfig = useMemo(
        () => (initialConfig ? JSON.parse(JSON.stringify(initialConfig)) : createInitialPromptEngineerConfig()),
        [initialConfig]
    );

    const resolvedResumeSnapshot = useMemo(() => {
        if (!resumeSnapshot || typeof resumeSnapshot !== 'object') {
            return null;
        }

        const stage = RESUMABLE_STAGES.has(resumeSnapshot.stage)
            ? resumeSnapshot.stage
            : null;

        const currentPhase = Number.isInteger(resumeSnapshot.currentPhase) && resumeSnapshot.currentPhase >= 0
            ? resumeSnapshot.currentPhase
            : null;

        const config = resumeSnapshot.config && typeof resumeSnapshot.config === 'object'
            ? JSON.parse(JSON.stringify(resumeSnapshot.config))
            : null;

        return {
            stage,
            currentPhase,
            config,
            instructionName: typeof resumeSnapshot.instructionName === 'string'
                ? resumeSnapshot.instructionName
                : '',
            draftText: typeof resumeSnapshot.draftText === 'string'
                ? resumeSnapshot.draftText
                : '',
            optimizedText: typeof resumeSnapshot.optimizedText === 'string'
                ? resumeSnapshot.optimizedText
                : '',
            generationSessionKey: typeof resumeSnapshot.generationSessionKey === 'string'
                ? resumeSnapshot.generationSessionKey
                : '',
            generationAttempt: Number.isInteger(resumeSnapshot.generationAttempt)
                ? Math.max(0, resumeSnapshot.generationAttempt)
                : 0,
            restartGenerationOnResume: Boolean(resumeSnapshot.restartGenerationOnResume),
            activeDraftVersion: resumeSnapshot.activeDraftVersion === 'optimized'
                ? 'optimized'
                : 'original'
        };
    }, [resumeSnapshot]);

    const { navigator } = useContext(UNSAFE_NavigationContext);
    const [currentPhase, setCurrentPhase] = useState(() => resolvedResumeSnapshot?.currentPhase ?? 0);
    const [stage, setStage] = useState(() => resolvedResumeSnapshot?.stage ?? (reopenMode ? 'interview' : 'landing'));
    const [config, setConfig] = useState(() => resolvedResumeSnapshot?.config ?? resolvedInitialConfig);
    const [draftText, setDraftText] = useState(() => resolvedResumeSnapshot?.draftText ?? (initialDraftText || ''));
    const [optimizedText, setOptimizedText] = useState(() => resolvedResumeSnapshot?.optimizedText ?? '');
    const [instructionName, setInstructionName] = useState(() => resolvedResumeSnapshot?.instructionName ?? (initialInstructionName || ''));
    const [activeDraftVersion, setActiveDraftVersion] = useState(() => resolvedResumeSnapshot?.activeDraftVersion ?? 'original');
    const [generationStatus, setGenerationStatus] = useState('idle');
    const [generationError, setGenerationError] = useState('');
    const [generationAttempt, setGenerationAttempt] = useState(() => {
        const seededAttempt = resolvedResumeSnapshot?.generationAttempt ?? 0;
        if (!onboardingRecoveryMode) {
            return seededAttempt;
        }

        const resumedStage = resolvedResumeSnapshot?.stage;
        const hasResumableGenerationContext = Boolean(
            resolvedResumeSnapshot?.generationSessionKey ||
            resolvedResumeSnapshot?.restartGenerationOnResume
        );

        if (resumedStage === 'progress' && hasResumableGenerationContext) {
            return Math.max(1, seededAttempt);
        }

        return seededAttempt;
    });
    const [generationSessionKey, setGenerationSessionKey] = useState(() => resolvedResumeSnapshot?.generationSessionKey ?? '');
    const [shouldRestoreGenerationSession, setShouldRestoreGenerationSession] = useState(() => Boolean(resolvedResumeSnapshot?.generationSessionKey));
    const [optimizationStatus, setOptimizationStatus] = useState('idle');
    const [optimizationError, setOptimizationError] = useState('');
    const [optimizationAttempt, setOptimizationAttempt] = useState(0);
    const [isGeneratingBusinessDescription, setIsGeneratingBusinessDescription] = useState(false);
    const [businessDescriptionGenerationError, setBusinessDescriptionGenerationError] = useState('');
    const [businessActionInterviewMetadata, setBusinessActionInterviewMetadata] = useState(null);
    const [businessActionMetadataError, setBusinessActionMetadataError] = useState('');
    const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
    const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
    const [isSavingForLater, setIsSavingForLater] = useState(false);
    const allowNextNavigationRef = useRef(false);
    const lastPublishedResumeSnapshotRef = useRef(null);
    const saveForLaterInvokerRef = useRef(null);
    const generationSessionPollRef = useRef(null);
    const executedGenerationAttemptRef = useRef(0);
    const onboardingRecoveryProgressStartedAtRef = useRef(0);
    const onboardingRecoveryTransitionTimerRef = useRef(null);
    const isOnboardingRecoveryMode = Boolean(onboardingRecoveryMode);
    const isJsonReviewDebugEnabled = useMemo(() => {
        if (!isJsonReviewDebugFlagEnabled || typeof window === 'undefined') {
            return false;
        }
        const params = new URLSearchParams(window.location.search);
        return params.get('peDebug') === '1';
    }, []);

    const initialConfigSnapshot = useMemo(() => JSON.stringify(resolvedInitialConfig), [resolvedInitialConfig]);
    const currentConfigSnapshot = useMemo(() => JSON.stringify(config), [config]);
    const normalizedInitialName = (initialInstructionName || '').trim();
    const normalizedCurrentName = (instructionName || '').trim();
    const normalizedInitialDraft = (initialDraftText || '').trim();
    const normalizedCurrentDraft = (draftText || '').trim();
    const canSaveForLater = flowContext !== 'onboarding' && Boolean(onSaveForLater) && Boolean(normalizedCurrentName);
    const contextMessage = resolveContextMessage(flowContext);
    const hasOptimizedVersion = Boolean(optimizedText.trim());
    const currentPersistenceSnapshot = useMemo(() => buildPersistenceSnapshot({
        stage,
        currentPhase,
        config,
        instructionName,
        draftText,
        optimizedText,
        generationSessionKey,
        activeDraftVersion
    }), [stage, currentPhase, config, instructionName, draftText, optimizedText, generationSessionKey, activeDraftVersion]);
    const initialPersistenceSnapshot = useMemo(() => buildPersistenceSnapshot({
        stage: resolvedResumeSnapshot?.stage ?? (reopenMode ? 'interview' : 'landing'),
        currentPhase: resolvedResumeSnapshot?.currentPhase ?? 0,
        config: resolvedResumeSnapshot?.config ?? resolvedInitialConfig,
        instructionName: resolvedResumeSnapshot?.instructionName ?? (initialInstructionName || ''),
        draftText: resolvedResumeSnapshot?.draftText ?? (initialDraftText || ''),
        optimizedText: resolvedResumeSnapshot?.optimizedText ?? '',
        generationSessionKey: resolvedResumeSnapshot?.generationSessionKey ?? '',
        activeDraftVersion: resolvedResumeSnapshot?.activeDraftVersion ?? 'original'
    }), [resolvedResumeSnapshot, reopenMode, resolvedInitialConfig, initialInstructionName, initialDraftText]);
    const [baselinePersistenceSnapshot, setBaselinePersistenceSnapshot] = useState(initialPersistenceSnapshot);

    const hasUnsavedProgress = useMemo(() => {
        return currentPersistenceSnapshot !== baselinePersistenceSnapshot;
    }, [
        currentPersistenceSnapshot,
        baselinePersistenceSnapshot
    ]);

    useEffect(() => {
        onUnsavedChange?.(hasUnsavedProgress);
    }, [hasUnsavedProgress, onUnsavedChange]);

    useEffect(() => {
        if (!onResumeSnapshotChange) {
            return;
        }

        if (!hasUnsavedProgress) {
            return;
        }

        const snapshot = {
            stage,
            currentPhase,
            config,
            instructionName,
            draftText,
            optimizedText,
            generationSessionKey,
            activeDraftVersion
        };

        const serialized = JSON.stringify(snapshot);
        if (serialized === lastPublishedResumeSnapshotRef.current) {
            return;
        }

        lastPublishedResumeSnapshotRef.current = serialized;
        onResumeSnapshotChange(snapshot);
    }, [
        onResumeSnapshotChange,
        hasUnsavedProgress,
        stage,
        currentPhase,
        config,
        instructionName,
        draftText,
        optimizedText,
        generationSessionKey,
        activeDraftVersion
    ]);

    useEffect(() => () => {
        onUnsavedChange?.(false);
    }, [onUnsavedChange]);

    useEffect(() => {
        setBaselinePersistenceSnapshot(initialPersistenceSnapshot);
    }, [initialPersistenceSnapshot]);

    const requestLeave = useCallback((leaveAction) => {
        if (!hasUnsavedProgress) {
            leaveAction?.();
            return;
        }
        setPendingLeaveAction(() => leaveAction);
        setDiscardDialogOpen(true);
    }, [hasUnsavedProgress]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!hasUnsavedProgress) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedProgress]);

    useEffect(() => {
        if (!navigator || !navigator.block) return;

        const unblock = navigator.block((tx) => {
            if (allowNextNavigationRef.current) {
                allowNextNavigationRef.current = false;
                tx.retry();
                return;
            }

            if (!hasUnsavedProgress) {
                tx.retry();
                return;
            }

            setPendingLeaveAction(() => () => tx.retry());
            setDiscardDialogOpen(true);
        });

        return () => unblock();
    }, [hasUnsavedProgress, navigator]);

    const handleUpdate = (section, field, value) => {
        setConfig((prev) => updatePromptEngineerField(prev, section, field, value));
    };

    const handleGenerateBusinessDescription = async ({ sourceUrl = '', mode = 'context', overwriteExisting = false }) => {
        setBusinessDescriptionGenerationError('');
        setIsGeneratingBusinessDescription(true);

        try {
            const generationConfig = overwriteExisting
                ? {
                    ...config,
                    business: {
                        ...config.business,
                        description: ''
                    }
                }
                : config;

            const result = await generateBusinessDescription(generationConfig, sourceUrl, mode);
            const generatedAt = result?.metadata?.generatedAt || new Date().toISOString();
            const generatedSourceType = result?.metadata?.sourceType || (sourceUrl?.trim() ? 'ai_url' : 'ai_context');

            setConfig((prev) => ({
                ...prev,
                business: {
                    ...prev.business,
                    description: result.description,
                    descriptionSource: generatedSourceType,
                    descriptionSourceUrl: result?.metadata?.sourceUrl || '',
                    descriptionGeneratedAt: generatedAt,
                    descriptionGenerationMeta: {
                        ...result.metadata,
                        generatedAt
                    }
                }
            }));
        } catch (error) {
            setBusinessDescriptionGenerationError(error?.message || 'Unable to generate business description right now.');
            throw error;
        } finally {
            setIsGeneratingBusinessDescription(false);
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadBusinessActionMetadata = async () => {
            setBusinessActionMetadataError('');
            try {
                const metadata = await getBusinessActionInterviewMetadata();
                if (cancelled) {
                    return;
                }
                setBusinessActionInterviewMetadata(metadata);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setBusinessActionInterviewMetadata({
                    phase: {
                        phaseTitle: 'Teach Your AI To Take Action',
                        phasePrompt: 'Based on what you\'ve told me so far, let\'s decide whether your AI should be able to interact with your connected business systems.'
                    },
                    businessActions: [],
                    hasEligibleConnectedServices: false
                });
                setBusinessActionMetadataError(error?.message || 'Unable to load connected service actions right now.');
            }
        };

        loadBusinessActionMetadata();
        return () => {
            cancelled = true;
        };
    }, []);

    const phaseSequence = useMemo(
        () => (isOnboardingRecoveryMode ? ONBOARDING_PHASE_SEQUENCE : FULL_PHASE_SEQUENCE),
        [isOnboardingRecoveryMode]
    );

    useEffect(() => {
        if (!phaseSequence.includes(currentPhase)) {
            if (isOnboardingRecoveryMode && currentPhase === ACTION_PHASE_INDEX) {
                setCurrentPhase(SUCCESS_PHASE_INDEX);
                return;
            }
            setCurrentPhase(phaseSequence[0]);
        }
    }, [phaseSequence, currentPhase, isOnboardingRecoveryMode]);

    const currentPhasePosition = useMemo(
        () => Math.max(0, phaseSequence.indexOf(currentPhase)),
        [phaseSequence, currentPhase]
    );

    const currentPhaseTitle = useMemo(() => {
        if (currentPhase === ACTION_PHASE_INDEX && businessActionInterviewMetadata?.phase?.phaseTitle) {
            return businessActionInterviewMetadata.phase.phaseTitle;
        }
        return undefined;
    }, [currentPhase, businessActionInterviewMetadata]);

    const currentPhasePrompt = useMemo(() => {
        if (currentPhase === ACTION_PHASE_INDEX && businessActionInterviewMetadata?.phase?.phasePrompt) {
            return businessActionInterviewMetadata.phase.phasePrompt;
        }
        return undefined;
    }, [currentPhase, businessActionInterviewMetadata]);

    const handleInterviewBack = () => {
        if (currentPhasePosition === 0) {
            if (reopenMode) {
                requestLeave(() => onExit?.());
            } else {
                setStage('landing');
            }
            return;
        }
        setCurrentPhase(phaseSequence[currentPhasePosition - 1]);
    };

    const handleInterviewContinue = () => {
        if (!isPhaseValid(currentPhase, config, instructionName)) return;

        const isLastPhase = currentPhasePosition === phaseSequence.length - 1;
        if (isLastPhase) {
            if (isJsonReviewDebugEnabled) {
                setStage('review');
            } else {
                startGeneration();
            }
            return;
        }

        setCurrentPhase(phaseSequence[currentPhasePosition + 1]);
    };

    const returnToPreGenerationStage = () => {
        if (isJsonReviewDebugEnabled) {
            setStage('review');
            return;
        }
        setCurrentPhase(phaseSequence[phaseSequence.length - 1]);
        setStage('interview');
    };

    const canContinue = isPhaseValid(currentPhase, config, instructionName);

    const handleBusinessActionSelectionsChange = (businessActionSelections) => {
        setConfig((prev) => updatePromptEngineerRootField(prev, 'businessActionSelections', businessActionSelections));
    };

    const stopGenerationSessionPolling = useCallback(() => {
        if (generationSessionPollRef.current) {
            window.clearInterval(generationSessionPollRef.current);
            generationSessionPollRef.current = null;
        }
    }, []);

    const clearOnboardingRecoveryTransitionTimer = useCallback(() => {
        if (onboardingRecoveryTransitionTimerRef.current) {
            window.clearTimeout(onboardingRecoveryTransitionTimerRef.current);
            onboardingRecoveryTransitionTimerRef.current = null;
        }
    }, []);

    const transitionProgressToDraftWithRecoveryHold = useCallback(() => {
        if (!isOnboardingRecoveryMode) {
            setStage((currentStage) => (currentStage === 'progress' ? 'draft' : currentStage));
            return;
        }

        const startedAt = onboardingRecoveryProgressStartedAtRef.current || Date.now();
        const elapsed = Date.now() - startedAt;
        const remainingVisibleMs = Math.max(0, ONBOARDING_RECOVERY_MIN_PROGRESS_VISIBLE_MS - elapsed);

        clearOnboardingRecoveryTransitionTimer();
        onboardingRecoveryTransitionTimerRef.current = window.setTimeout(() => {
            setStage((currentStage) => (currentStage === 'progress' ? 'draft' : currentStage));
            onboardingRecoveryTransitionTimerRef.current = null;
        }, remainingVisibleMs);
    }, [isOnboardingRecoveryMode, clearOnboardingRecoveryTransitionTimer]);

    useEffect(() => {
        if (stage === 'progress' && isOnboardingRecoveryMode) {
            onboardingRecoveryProgressStartedAtRef.current = Date.now();
        }
    }, [stage, isOnboardingRecoveryMode]);

    const createGenerationSessionKey = useCallback(() => {
        if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
            return window.crypto.randomUUID();
        }
        return `pe-session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }, []);

    const applyRecoveredGenerationSession = useCallback((session) => {
        if (!session) {
            return;
        }

        if (typeof session.instructionName === 'string' && session.instructionName.trim()) {
            setInstructionName(session.instructionName.trim());
        }

        if (typeof session.artifactContent === 'string' && session.artifactContent.trim()) {
            setDraftText(session.artifactContent);
        }

        if (session.status === 'COMPLETE') {
            setGenerationStatus('success');
            setGenerationError('');
            setShouldRestoreGenerationSession(false);
            stopGenerationSessionPolling();
            transitionProgressToDraftWithRecoveryHold();
            return;
        }

        if (session.status === 'GENERATING') {
            setGenerationStatus('running');
            if (stage !== 'progress') {
                setStage('progress');
            }

            if (!generationSessionPollRef.current) {
                generationSessionPollRef.current = window.setInterval(async () => {
                    try {
                        const refreshedSession = await getPromptEngineerGenerationSession(session.sessionKey);
                        if (!refreshedSession) {
                            return;
                        }

                        if (refreshedSession.status === 'COMPLETE') {
                            if (typeof refreshedSession.artifactContent === 'string' && refreshedSession.artifactContent.trim()) {
                                setDraftText(refreshedSession.artifactContent);
                            }
                            setGenerationStatus('success');
                            setGenerationError('');
                            setShouldRestoreGenerationSession(false);
                            stopGenerationSessionPolling();
                            transitionProgressToDraftWithRecoveryHold();
                            return;
                        }

                        if (refreshedSession.status === 'FAILED') {
                            setGenerationStatus('error');
                            setGenerationError(refreshedSession.lastError || 'Unable to generate instructions at this time.');
                            setShouldRestoreGenerationSession(false);
                            stopGenerationSessionPolling();
                        }
                    } catch (error) {
                        setGenerationStatus('error');
                        setGenerationError(error?.message || 'Unable to recover generation progress right now.');
                        setShouldRestoreGenerationSession(false);
                        stopGenerationSessionPolling();
                    }
                }, 1500);
            }
            return;
        }

        setGenerationStatus('error');
        setGenerationError(session.lastError || 'Unable to generate instructions at this time.');
        setShouldRestoreGenerationSession(false);
        stopGenerationSessionPolling();
    }, [stage, stopGenerationSessionPolling, transitionProgressToDraftWithRecoveryHold]);

    useEffect(() => {
        if (!shouldRestoreGenerationSession || !generationSessionKey) {
            return;
        }

        let cancelled = false;

        const restoreGenerationSession = async () => {
            try {
                const session = await getPromptEngineerGenerationSession(generationSessionKey);
                if (cancelled) {
                    return;
                }
                applyRecoveredGenerationSession(session);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setGenerationStatus('error');
                setGenerationError(error?.message || 'Unable to recover generation progress right now.');
                setShouldRestoreGenerationSession(false);
            }
        };

        restoreGenerationSession();

        return () => {
            cancelled = true;
        };
    }, [shouldRestoreGenerationSession, generationSessionKey, applyRecoveredGenerationSession]);

    useEffect(() => () => {
        stopGenerationSessionPolling();
        clearOnboardingRecoveryTransitionTimer();
    }, [stopGenerationSessionPolling, clearOnboardingRecoveryTransitionTimer]);

    useEffect(() => {
        if (!isOnboardingRecoveryMode) {
            return;
        }

        if (stage !== 'progress' || generationAttempt > 0) {
            return;
        }

        if (!generationSessionKey && !resolvedResumeSnapshot?.restartGenerationOnResume) {
            return;
        }

        // Prevent passive progress restore by ensuring a resumable run id is present.
        setGenerationAttempt(1);
    }, [
        isOnboardingRecoveryMode,
        stage,
        generationAttempt,
        generationSessionKey,
        resolvedResumeSnapshot?.restartGenerationOnResume
    ]);

    const moveToDraft = () => {
        devLog('fallback_basic_draft_used', summarizeConfig(config));
        if (!draftText.trim()) {
            setDraftText(buildPlaceholderInstructions(config));
        }
        if (!instructionName.trim()) {
            setInstructionName(buildDefaultInstructionName(config));
        }
        setGenerationStatus('idle');
        setStage('draft');
    };

    useEffect(() => {
        if (stage !== 'progress' || generationAttempt === 0 || shouldRestoreGenerationSession) return;

        // Ensure each generation attempt runs exactly once, even if state updates
        // (for example generationSessionKey hydration) occur while still in progress.
        if (executedGenerationAttemptRef.current === generationAttempt) {
            return;
        }
        executedGenerationAttemptRef.current = generationAttempt;

        let cancelled = false;

        const runGeneration = async () => {
            setGenerationError('');
            setGenerationStatus('running');

            try {
                const generationResponse = await generatePromptInstructions(config, {
                    instructionName: instructionName.trim(),
                    configVersion: config?.version || '',
                    generationType: 'prompt_engineer',
                    instructionId: existingInstructionId || null,
                    reopenStatus: Boolean(reopenMode),
                    optimizationStatus: optimizationStatus || 'not_started',
                    generationSessionKey
                });
                if (cancelled) return;

                // A new engineering run must replace any previously loaded draft,
                // including reopen-mode content seeded from the saved instruction.
                setGenerationSessionKey(generationResponse?.generationSessionKey || generationSessionKey);
                setDraftText(generationResponse?.instructions || '');
                setOptimizedText('');
                setActiveDraftVersion('original');
                if (!instructionName.trim()) {
                    setInstructionName(buildDefaultInstructionName(config));
                }
                setGenerationStatus('success');
            } catch (error) {
                if (cancelled) return;
                setGenerationError(error?.message || 'Unable to generate instructions at this time.');
                setGenerationStatus('error');
            }
        };

        runGeneration();

        return () => {
            cancelled = true;
        };
    }, [
        stage,
        generationAttempt,
        shouldRestoreGenerationSession,
        config,
        instructionName,
        optimizationStatus,
        reopenMode,
        generationSessionKey,
        existingInstructionId
    ]);

    const startGeneration = () => {
        setGenerationError('');
        setOptimizedText('');
        setActiveDraftVersion('original');
        setGenerationStatus('running');
        setStage('progress');
        setShouldRestoreGenerationSession(false);
        stopGenerationSessionPolling();
        setGenerationSessionKey(createGenerationSessionKey());
        setGenerationAttempt((prev) => prev + 1);
    };

    const skipOptimization = () => {
        setActiveDraftVersion('original');
        setStage('optimizedDraft');
    };

    const moveToOptimize = () => {
        setOptimizationStatus('idle');
        setOptimizationError('');
        setStage('optimize');
    };

    const handleSaveForLater = useCallback(async ({ leaveAfterSave = false, leaveAction = null } = {}) => {
        if (!canSaveForLater || isSavingForLater) {
            return;
        }

        const contentToSave = activeDraftVersion === 'optimized' && optimizedText.trim()
            ? optimizedText
            : draftText;

        const sourceType = config?.source || 'ai_prompt_engineer';
        const sourceVersion = config?.version || null;
        const promptEngineerMeta = {
            lastSavedFrom: 'prompt_engineer',
            selectedDraftVersion: activeDraftVersion,
            hasOptimizedVersion,
            reopenedFromExisting: Boolean(reopenMode),
            generationSessionKey,
            workspaceSnapshot: {
                stage,
                currentPhase,
                draftText,
                optimizedText,
                activeDraftVersion
            },
            savedAt: new Date().toISOString()
        };

        setIsSavingForLater(true);
        try {
            await onSaveForLater?.({
                id: existingInstructionId || undefined,
                name: instructionName.trim(),
                content: contentToSave,
                sourceType,
                sourceVersion,
                promptEngineerConfig: config,
                promptEngineerMeta
            }, {
                leaveAfterSave,
                leaveAction
            });

            setBaselinePersistenceSnapshot(currentPersistenceSnapshot);
            lastPublishedResumeSnapshotRef.current = null;
            onResumeSnapshotClear?.();

            if (leaveAfterSave) {
                const leaveAction = pendingLeaveAction;
                setDiscardDialogOpen(false);
                setPendingLeaveAction(null);
                allowNextNavigationRef.current = true;
                leaveAction?.();
            }
        } finally {
            setIsSavingForLater(false);
        }
    }, [
        canSaveForLater,
        isSavingForLater,
        activeDraftVersion,
        optimizedText,
        draftText,
        config,
        reopenMode,
        generationSessionKey,
        stage,
        currentPhase,
        existingInstructionId,
        instructionName,
        onSaveForLater,
        currentPersistenceSnapshot,
        onResumeSnapshotClear,
        pendingLeaveAction,
        hasOptimizedVersion
    ]);

    useEffect(() => {
        saveForLaterInvokerRef.current = handleSaveForLater;
    }, [handleSaveForLater]);

    useEffect(() => {
        if (!onSaveForLaterRequestChange) {
            return undefined;
        }

        const registeredHandler = canSaveForLater
            ? (options) => saveForLaterInvokerRef.current?.(options)
            : null;

        if (canSaveForLater) {
            onSaveForLaterRequestChange(registeredHandler);
        } else {
            onSaveForLaterRequestChange(null);
        }

        return () => {
            onSaveForLaterRequestChange(null);
        };
    }, [onSaveForLaterRequestChange, canSaveForLater]);

    useEffect(() => {
        if (stage !== 'optimize' || optimizationStatus !== 'running' || optimizationAttempt === 0) return;

        let cancelled = false;

        const runOptimization = async () => {
            setOptimizationError('');

            try {
                const optimized = await optimizePromptInstructions(config, draftText);
                if (cancelled) return;

                if (!optimizedText.trim()) {
                    setOptimizedText(optimized);
                } else {
                    setOptimizedText(optimized);
                }
                setOptimizationStatus('success');
            } catch (error) {
                if (cancelled) return;
                setOptimizationError(error?.message || 'Unable to optimize instructions at this time.');
                setOptimizationStatus('error');
            }
        };

        runOptimization();

        return () => {
            cancelled = true;
        };
    }, [stage, optimizationStatus, optimizationAttempt, config, draftText, optimizedText]);

    const startOptimization = () => {
        setStage('optimize');
        setOptimizationError('');
        setOptimizationStatus('running');
        setOptimizationAttempt((prev) => prev + 1);
    };

    const handleOptimizationComplete = () => {
        setActiveDraftVersion('optimized');
        setStage('optimizedDraft');
    };

    const returnToOptimizeScreen = () => {
        setOptimizationStatus('idle');
        setOptimizationError('');
        setStage('optimize');
    };

    const handleContinueWithOriginalDraft = () => {
        setActiveDraftVersion('original');
        setStage('optimizedDraft');
    };

    const handleSaveActiveDraft = async () => {
        const contentToSave = activeDraftVersion === 'optimized' && optimizedText.trim()
            ? optimizedText
            : draftText;

        const sourceType = config?.source || 'ai_prompt_engineer';
        const sourceVersion = config?.version || null;
        const promptEngineerMeta = {
            lastSavedFrom: 'prompt_engineer',
            selectedDraftVersion: activeDraftVersion,
            hasOptimizedVersion,
            reopenedFromExisting: Boolean(reopenMode),
            generationSessionKey,
            savedAt: new Date().toISOString(),
            businessDescriptionGeneration: config?.business?.descriptionGenerationMeta || null,
            businessDescriptionSource: config?.business?.descriptionSource || 'manual',
            businessDescriptionSourceUrl: config?.business?.descriptionSourceUrl || '',
            businessDescriptionAdditionalContext: config?.business?.additionalContext || '',
            businessDescriptionAdditionalContextLength: (config?.business?.additionalContext || '').trim().length
        };

        const savedInstruction = await onSaveInstruction({
            id: existingInstructionId || undefined,
            name: instructionName.trim(),
            content: contentToSave,
            sourceType,
            sourceVersion,
            promptEngineerConfig: config,
            promptEngineerMeta
        });
        if (savedInstruction === null) {
            return;
        }
        setBaselinePersistenceSnapshot(currentPersistenceSnapshot);
        setGenerationSessionKey('');
        setShouldRestoreGenerationSession(false);
        stopGenerationSessionPolling();
        lastPublishedResumeSnapshotRef.current = null;
        onResumeSnapshotClear?.();
    };

    const handleContinueEditing = () => {
        setDiscardDialogOpen(false);
        setPendingLeaveAction(null);
    };

    const handleDiscardAndLeave = () => {
        const leaveAction = pendingLeaveAction;
        setDiscardDialogOpen(false);
        setPendingLeaveAction(null);
        allowNextNavigationRef.current = true;
        setGenerationSessionKey('');
        setShouldRestoreGenerationSession(false);
        stopGenerationSessionPolling();
        lastPublishedResumeSnapshotRef.current = null;
        onResumeSnapshotClear?.();
        leaveAction?.();
    };

    return (
        <Box>
            {(contextMessage || (canSaveForLater && showInlineSaveForLaterAction)) && (
                <Box sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                        <Box>
                            {contextMessage && (
                                <Typography variant="body2" color="text.secondary">
                                    {contextMessage}
                                </Typography>
                            )}
                        </Box>
                        {canSaveForLater && showInlineSaveForLaterAction && (
                            <Button
                                variant="outlined"
                                onClick={() => handleSaveForLater({ leaveAfterSave: false })}
                                disabled={isSaving || isSavingForLater || !normalizedCurrentName}
                            >
                                {isSavingForLater ? 'Saving Draft…' : 'Save For Later'}
                            </Button>
                        )}
                    </Stack>
                </Box>
            )}


            {stage === 'landing' && (
                <PromptEngineerLanding
                    onStart={() => setStage('interview')}
                    onBack={() => requestLeave(() => onExit?.())}
                />
            )}

            {stage === 'interview' && (
                <PromptEngineerInterview
                    currentPhase={currentPhase}
                    phasePosition={currentPhasePosition}
                    phaseCount={phaseSequence.length}
                    phaseTitle={currentPhaseTitle}
                    phasePrompt={currentPhasePrompt}
                    config={config}
                    instructionName={instructionName}
                    canContinue={canContinue}
                    onBack={handleInterviewBack}
                    onContinue={handleInterviewContinue}
                    onInstructionNameChange={setInstructionName}
                    onUpdate={handleUpdate}
                    onGenerateBusinessDescription={handleGenerateBusinessDescription}
                    isGeneratingBusinessDescription={isGeneratingBusinessDescription}
                    businessDescriptionGenerationError={businessDescriptionGenerationError}
                    businessActionInterviewMetadata={businessActionInterviewMetadata}
                    businessActionMetadataError={businessActionMetadataError}
                    onBusinessActionSelectionsChange={handleBusinessActionSelectionsChange}
                />
            )}

            {stage === 'review' && isJsonReviewDebugEnabled && (
                <PromptEngineerReview
                    config={config}
                    onBack={() => setStage('interview')}
                    onContinue={startGeneration}
                />
            )}

            {stage === 'progress' && (
                <PromptEngineerProgress
                    generationStatus={generationStatus}
                    errorMessage={generationError}
                    runId={generationAttempt}
                    recoveryMode={isOnboardingRecoveryMode}
                    recoveryMessage="Recovering your instruction generation from your last onboarding session..."
                    onTryAgain={startGeneration}
                    onBackToReview={returnToPreGenerationStage}
                    backButtonLabel={isJsonReviewDebugEnabled ? 'Back to Review' : 'Back to Interview'}
                    onUseBasicDraft={moveToDraft}
                    onReadyToContinue={moveToDraft}
                />
            )}

            {stage === 'draft' && (
                <PromptEngineerDraft
                    draftText={draftText}
                    onBack={returnToPreGenerationStage}
                    onContinue={moveToOptimize}
                    onSkipOptimization={skipOptimization}
                    onChangeDraft={setDraftText}
                />
            )}

            {stage === 'optimize' && (
                <PromptEngineerOptimize
                    optimizationStatus={optimizationStatus}
                    errorMessage={optimizationError}
                    runId={optimizationAttempt}
                    onStart={startOptimization}
                    onTryAgain={startOptimization}
                    onContinueWithOriginal={handleContinueWithOriginalDraft}
                    onBack={() => setStage('draft')}
                    onReadyToContinue={handleOptimizationComplete}
                />
            )}

            {stage === 'optimizedDraft' && (
                <PromptEngineerOptimizedDraft
                    instructionName={instructionName}
                    originalInstructions={draftText}
                    optimizedInstructions={optimizedText}
                    hasOptimizedVersion={hasOptimizedVersion}
                    activeVersion={hasOptimizedVersion ? activeDraftVersion : 'original'}
                    onChangeActiveVersion={setActiveDraftVersion}
                    onChangeOriginalInstructions={setDraftText}
                    onChangeOptimizedInstructions={setOptimizedText}
                    onBack={hasOptimizedVersion ? returnToOptimizeScreen : () => setStage('draft')}
                    onSave={handleSaveActiveDraft}
                    isSaving={isSaving}
                />
            )}

            <Dialog
                open={discardDialogOpen}
                onClose={handleContinueEditing}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Discard AI Instruction Architect Session?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Your interview responses have not been saved. Leaving now will permanently discard your progress. This cannot be recovered.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    {canSaveForLater && (
                        <Button onClick={() => handleSaveForLater({ leaveAfterSave: true })} variant="contained" disabled={isSaving || isSavingForLater}>
                            {isSavingForLater ? 'Saving Draft…' : 'Save and Leave'}
                        </Button>
                    )}
                    <Button onClick={handleDiscardAndLeave} color="error" variant="contained">Leave Without Saving</Button>
                    <Button onClick={handleContinueEditing} variant="outlined">Continue Editing</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
